import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Flex, notification, Space, Spin, Typography } from "antd";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import RequestAppAction from "src/store/slices/appActions";
import { initiateCreateCompany } from "src/store/slices/features/createCompany";
import { getCreateCompanyData, getCreateCompanyStates } from "src/store/selectors/features/createCompany";
import { IStripeProducts } from "src/constants/types";
import { RequestedDidNumberType } from "src/constants/subscription";
import { loadGTM } from "src/utils/loadGTM";
import { formatAddress } from "src/utils/format";
import { countryCodeToCurrency, Status } from "src/constants/common";
import Plans from "src/components/subscribe/steps/Plans";
import Step1 from "src/components/subscribe/steps/Step1";
import Step2 from "src/components/subscribe/steps/Step2";
import Step3 from "src/components/subscribe/steps/Step3";
import Step5 from "src/components/subscribe/steps/Step5";
import styles from "./subscribe.module.scss";
import "./subscribe.scss";

const { Text } = Typography;

const Subscribe: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const REACT_APP_STRIPE_PUBLISHABLE_API_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_API_KEY ?? "";

  const createCompanyState: any = useSelector(getCreateCompanyStates);
  const createCompanyData: any = useSelector(getCreateCompanyData);

  const hasTriggeredRef = useRef(false);
  const isProcessingPaymentRef = useRef(false);

  const [selectedPlan, setSelectedPlan] = useState<IStripeProducts | null | undefined>(null);
  const [isFreePlan, setIsFreePlan] = useState<boolean>(true);
  const [subscriptionStep, setSubscriptionStep] = useState<number>(0);
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>();
  const [address, setAddress] = useState<any>(null);
  const [numUsersDids, setNumUsersDids] = useState<any>({ 1: 1 });
  const [numberTypes, setNumberTypes] = useState<any>({});
  const [signUpUserFormData, setSignUpUserFormData] = useState<any>(null);
  const [signUpUserData, setSignUpUserData] = useState<any>({});
  const [additional, setAdditional] = useState<any>({ numUser: 0, numDid: 0 });
  const [paymentIntentSecret, setPaymentIntentSecret] = useState<string>("");
  const [stripe, setStripe] = useState<any>(null);
  const [cardElement, setCardElement] = useState<null | React.JSX.Element>(null);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [stripePromise, setStripePromise] = useState(() => 
    loadStripe(REACT_APP_STRIPE_PUBLISHABLE_API_KEY)
  );
  const [cardSetupResult, setCardSetupResult] = useState<any>(null);
  const [resetPaymentIntent, setResetPaymentIntent] = useState<boolean>(false);
  const [isSettingUpPaymentMethod, setIsSettingUpPaymentMethod] = useState(false);

  const isStripeReady = !!stripe && !!cardElement && !!paymentIntentSecret;

  useEffect(() => {
    loadGTM('GTM-WMPPPHZW');
  }, []);

  useEffect(() => {
    if (subscriptionStep === 4) {
      hasTriggeredRef.current = false;
      isProcessingPaymentRef.current = false;

      if (!isFreePlan) {
        onReassignPaymentIntent();
      }
    }

    if (subscriptionStep < 5) {
      hasTriggeredRef.current = false;
    }
  }, [subscriptionStep, isFreePlan, isStripeReady]);

  useEffect(() => {
    const freePlan = !selectedPlan?.monthlyRate
      || selectedPlan?.monthlyRate === 0
      || selectedPlan?.monthlyRate === "0"
      || selectedPlan?.monthlyRate === "0.00";

    if (isFreePlan !== freePlan) {
      setIsFreePlan(freePlan);
    }
  }, [selectedPlan]);

  const resetStripeState = () => {
    setStripe(undefined);
    setCardElement(null);
    setPaymentIntentSecret("");
  };

  const onReassignPaymentIntent = () => {
    setResetPaymentIntent(false);
    setTimeout(() => setResetPaymentIntent(true), 0);
  };

  const prevStep = (n?: number) => {
    const newStep = typeof n === "number" ? n : subscriptionStep - 1;
    setSubscriptionStep(newStep);

    if (newStep === 4) {
      resetStripeState();
      hasTriggeredRef.current = false;

      if (paymentIntentSecret) {
        onReassignPaymentIntent();
      }
    }
  };

  const nextStep = (n?: number) => {
    if (n !== null && n !== undefined) {
      setSubscriptionStep(n);
    } else {
      setSubscriptionStep(subscriptionStep + 1);
    }
  }

  const sendOtp = (email?: string, func?: any, data?: any) => {
    setDisableButton(true);
    if (!(email && data) && !signUpUserData?.users) {
      notification.error({ message: "Unknown error." });
      setDisableButton(false);
      return;
    }

    let sendEmail = "";
    let firstName = "";
    let lastName = "";

    if (email && data) {
      sendEmail = email;
      firstName = data.firstName;
      lastName = data.lastName;
    } else if (signUpUserData?.users && signUpUserData.users[0]) {
      sendEmail = signUpUserData.users[0]?.email || "";
      firstName = signUpUserData.users[0]?.firstName || "";
      lastName = signUpUserData.users[0]?.lastName || "";
    }

    if (!sendEmail || !firstName || !lastName) {
      notification.error({ message: t("fields.emailRequired") });
      setDisableButton(false);
      return;
    }

    dispatch(RequestAppAction.sendOtp({
      data: {
        email: sendEmail,
        firstName: firstName,
        lastName: lastName,
      },
      cbSuccess: (res: any) => {
        const successMessage = res?.data?.message ?? t("notification.otpEmailSent");
        notification.success({ message: successMessage });
        if (func) func();
        setDisableButton(false);
      },
      cbFailure: (e: string) => {
        notification.error({ message: e });
        if (func) func();
        setDisableButton(false);
      },
    }));
  };

  const onStage1 = (values: any) => {
    setDisableButton(true);

    if (!selectedPlan?.id) {
      notification.error({
        message: "Plan is not selected. Please start over the subscription flow to make sure that the plan is selected."
      });
      return;
    }

    const { email, firstName, lastName } = values;

    const data = {
      selectedPlan,
      users: [
        {
          firstName: firstName,
          lastName: lastName,
          email: email,
        }
      ]
    };
    setSignUpUserData(data);

    sendOtp(email, nextStep, { firstName, lastName });
  };

  const onStage2 = (values: any, form: any) => {
    const { otp } = values;

    if (!signUpUserData?.users[0]?.email) {
      return notification.error({ message: "Unknown error." });
    }

    dispatch(RequestAppAction.verifyOtp({
      data: {
        email: signUpUserData.users[0].email,
        otp: otp
      },
      cbSuccess: (res: any) => {
        const successMessage = res?.data?.message ?? t("notification.otpVerified");
        notification.success({ message: successMessage });
        nextStep();
      },
      cbFailure: (e: string) => {
        notification.error({ message: e });
        form.setFields([
          { name: "otp", errors: [e ?? t("fields.enterOtpError")] }
        ]);
      },
    }));
  };

  const onStage3 = (values: any) => {
    const {
      companyName,
      timezone,
      country,
      province,
      state,
      city,
      postalCode,
      billingEmailAddress,
      howDidYouHear
    } = values;

    const numUsers = Object.keys(values).filter((key) =>
      key.startsWith("user_") && key.endsWith("_username")
    ).length;

    const address = formatAddress(
      postalCode,
      country ?? selectedCountry,
      province,
      state,
      city,
    );
    setAddress(address);

    const payloadData: any = {
      stripeProductId: signUpUserData?.selectedPlan?.id ?? null,
      status: Status.PENDING,
      name: companyName,
      timeZone: timezone,
      ...address,
      currency: address?.country ? countryCodeToCurrency[address.country] : "CAD",
      users: [],
      billingEmailAddress: billingEmailAddress ?? "",
      notes: howDidYouHear,
      billedExternally: false,
    };

    if (!payloadData?.stripeProductId) {
      notification.error({
        message: "Plan is not selected. Please start over the subscription flow to make sure that the plan is selected."
      });
      return;
    }

    let totalNumRequestedDids = 0;
    const existingDids: string[] = [];

    for (let i = 1; i <= numUsers; i++) {
      if (i === 1) {
        if (!billingEmailAddress || billingEmailAddress === "") {
          payloadData.billingEmailAddress = values[`user_${i}_email`];
        }
      }

      const user: any = {
        name: values[`user_${i}_contactName`],
        status: Status.ACTIVE,
        email: values[`user_${i}_email`],
        username: values[`user_${i}_username`],
        roleId: 2,
        dids: [],
      };

      const numDids = Object.keys(numberTypes).filter((key) =>
        key.startsWith(`user_${i}_did_`) && (key.endsWith("_numberType"))
      ).length;

      for (let j = 1; j <= numDids; j++) {
        let number = values[`user_${i}_did_${j}_addNumber`];
        let numberType = numberTypes[`user_${i}_did_${j}_numberType`] ?? RequestedDidNumberType.LOCAL;
        let specialRequests = values[`user_${i}_did_${j}_specialRequests`];
        let localAddress = null;

        if (numberType === RequestedDidNumberType.REQUESTED) {
          number = "";
          delete values[`user_${i}_did_${j}_addNumber`];
          specialRequests = t("subscribeStep0.requestedNumber") + " " + specialRequests;
        } else if (
          numberType === RequestedDidNumberType.EXISTING
          && number
          && number !== ""
        ) {
          specialRequests = "";
          if (values[`user_${i}_did_${j}_specialRequests`]) {
            delete values[`user_${i}_did_${j}_specialRequests`];
          }
          if (!existingDids.includes(number)) {
            existingDids.push(number);
          }
        } else if (numberType === RequestedDidNumberType.LOCAL) {
          localAddress = {
            country: values[`user_${i}_did_${j}_local_country`],
            state: values[`user_${i}_did_${j}_local_country`] === "CA"
              ? values[`user_${i}_did_${j}_local_province`]
              : values[`user_${i}_did_${j}_local_state`],
            city: values[`user_${i}_did_${j}_local_city`],
          };
          totalNumRequestedDids++;
        } else {
          totalNumRequestedDids++;
        }

        values[`user_${i}_did_${j}_numberType`] = numberType;

        const did: any = {
          did: number ?? "",
          status: !!number ? Status.PENDING : Status.INACTIVE,
          email: values[`user_${i}_email`],
          numberType: numberType,
          specialRequests,
          localAddress,
        };
        user.dids.push(did);
      }
      payloadData.users.push(user);
    }

    setSignUpUserFormData(values);
    setSignUpUserData(payloadData);

    const totalNumExistingDids = Array.isArray(existingDids) ? existingDids.length : 0;
    setAdditional({ numUser: numUsers - 1, numDid: (totalNumRequestedDids + totalNumExistingDids) - 1 });

    if (isFreePlan) {
      nextStep(5);
    } else {
      nextStep();
    }
  };

  const onStage4 = async (stage4Stripe: any, stage4CardElement: null | React.JSX.Element) => {
    if (isFreePlan) {
      nextStep();
      return;
    }

    const stripeLoaded = stage4Stripe || stripe;
    const cardElementLoaded = stage4CardElement || cardElement;

    if (!stripeLoaded || !cardElementLoaded || !paymentIntentSecret) {
      notification.error({ message: "Payment method set up system is not ready. Please ensure card details are filled in correctly or try again after a few seconds." });
      onReassignPaymentIntent();
      return;
    }

    if (!hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      setIsSettingUpPaymentMethod(true);

      try {
        await onPaymentMethod(stripeLoaded, cardElementLoaded);
      } catch (err) {
        console.error("Unhandled confirmCardSetup error:", err);
      } finally {
        hasTriggeredRef.current = false;
        setIsSettingUpPaymentMethod(false);
      }
    }
  };

  const onStage5 = () => {
    setDisableButton(true);

    if (!signUpUserData?.stripeProductId) {
      notification.error({
        message: "Plan is not selected. Please start over the subscription flow to make sure that the plan is selected."
      });
      return;
    }

    // Create account
    if (!createCompanyState?.isLoading) {
      onSubscribe();
    }
  };

  const onPaymentMethod = async (stage4Stripe?: any, stage4CardElement?: null | React.JSX.Element) => {
    // Prevent confirmCardSetup from being called multiple times
    if (isProcessingPaymentRef?.current) return;
    isProcessingPaymentRef.current = true;

    const stripeLoaded = stage4Stripe || stripe;
    const cardElementLoaded = stage4CardElement || cardElement;

    if (!address || !stripeLoaded || !cardElementLoaded || !paymentIntentSecret) {
      notification.error({ message: "Missing billing or card info. Please re-enter your card." });
      isProcessingPaymentRef.current = false;
      return;
    }

    try {
      const result = await stripeLoaded.confirmCardSetup(
        paymentIntentSecret,
        {
          payment_method: {
            card: cardElementLoaded,
            billing_details: {
              address: {
                country: address.country,
                state: address.state,
                city: address.city,
                line1: address.line1,
                line2: address.line2,
                postal_code: address.postalCode,
              },
              email: signUpUserData.billingEmailAddress,
              name: signUpUserData.companyName,
            }
          }
        }
      );

      if (result.error) {
        notification.error({ message: result.error.message || "Error during payment setup" });
        setCardSetupResult(null);
        onReassignPaymentIntent();
        prevStep(4);
        isProcessingPaymentRef.current = false;
      } else {
        setCardSetupResult(result);
        nextStep();
      }
    } catch (e: any) {
      notification.error({ message: e?.message || "Unexpected error during Stripe setup." });
      onReassignPaymentIntent();
      prevStep(4);
      isProcessingPaymentRef.current = false;
      setDisableButton(false);
    }
  };

  const subscribeExistingAccount = (accountId: any) => {
    dispatch(RequestAppAction.subscribeExistingAccount({
      data: {
        companyId: +accountId,
        ...signUpUserData
      },
      cbSuccess: () => {
        nextStep();
        setDisableButton(false);
        isProcessingPaymentRef.current = false;
      },
      cbFailure: (e: string) => {
        notification.error({ message: e });
        nextStep();
        setDisableButton(false);
        isProcessingPaymentRef.current = false;
      },
    }));
  };

  const onSubscribe = () => {
    if (!isFreePlan && !cardSetupResult?.setupIntent?.payment_method) {
      notification.error({ message: "Payment method was not set up successfully. Please go back to previous step and try again." });
      prevStep(5);
      isProcessingPaymentRef.current = false;
      setDisableButton(false);
      return;
    }

    dispatch(initiateCreateCompany({
      data: {
        ...signUpUserData,
        isWebSubscriptionFlow: true,
      },
      cbSuccess: (res: any) => {
        const accountId = res?.data?.data?.id;

        if (!accountId) {
          notification.error({ message: "Account creation failed. Please try again." });
          prevStep(5);
          isProcessingPaymentRef.current = false;
        } else {
          if (isFreePlan || !cardSetupResult?.setupIntent?.payment_method) {
            subscribeExistingAccount(accountId);
          } else {
            dispatch(RequestAppAction.createStripePaymentMethod({
              id: +accountId,
              paymentMethodId: cardSetupResult.setupIntent.payment_method,
              cbSuccess: () => {
                subscribeExistingAccount(accountId);
              },
              cbFailure: (e: string) => {
                notification.error({ message: e || "Unexpected error during card setup" });
                prevStep(5);
                isProcessingPaymentRef.current = false;
                setDisableButton(false);
              },
            }));
          }
        }
      },
      cbFailure: (e: string) => {
        notification.error({ message: e + " Contact admin for the assistance." });
        prevStep(5);
      },
    }));
  };

  return (
    <Flex
      justify="start"
      align="center"
      gap={10}
      vertical
      style={{ width: "100%", overflowY: "auto" }}
      className={styles.subscribe_form_container}
    >
      <Space
        direction="vertical"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "1440px",
          height: "100%"
        }}
        className={`${styles.subscribe_form_space} subscribe_form_space`}
      >
        <Text
          style={{ textAlign: "center", color: "#f7941d" }}
          className="font-size-16 font-weight-800"
        >
          {disableButton && "Don't leave the page until the process is complete."}
        </Text>

        <Spin spinning={disableButton}>
          {subscriptionStep === 0 ? (
            <Plans setSelectedPlan={setSelectedPlan} nextStep={nextStep}  />
          ) : subscriptionStep === 1 ? (
            <Step1
              userInfo={signUpUserData}
              onFinish={onStage1}
              disableButton={disableButton}
            />
          ) : subscriptionStep === 2 ? (
            <Step2
              email={signUpUserData.users[0].email}
              sendOtp={sendOtp}
              prevStep={prevStep}
              onFinish={onStage2}
            />
          ) : subscriptionStep === 3 ? (
            <Step3
              userData={signUpUserData}
              isFreePlan={isFreePlan}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              signUpUserFormData={signUpUserFormData}
              numUsersDids={numUsersDids}
              setNumUsersDids={setNumUsersDids}
              numberTypes={numberTypes}
              setNumberTypes={setNumberTypes}
              prevStep={prevStep}
              onFinish={onStage3}
              selectedPlan={selectedPlan}
            />
          ) : (
            <Elements stripe={stripePromise}>
              <Step5
                prevStep={prevStep}
                subscriptionStep={subscriptionStep}
                resetPaymentIntent={resetPaymentIntent}
                setResetPaymentIntent={setResetPaymentIntent}
                setPaymentIntentSecret={setPaymentIntentSecret}
                setStripe={setStripe}
                setCardElement={setCardElement}
                userData={signUpUserData}
                setUserData={setSignUpUserData}
                signUpUserFormData={signUpUserFormData}
                setSignUpUserFormData={setSignUpUserFormData}
                numUsersDids={numUsersDids}
                selectedPlan={selectedPlan}
                isFreePlan={isFreePlan}
                additional={additional}
                disableStep4={isSettingUpPaymentMethod}
                disableButton={disableButton}
                onStage4={onStage4}
                onFinish={onStage5}
              />
            </Elements>
          )}
        </Spin>
      </Space>

      {/* {selectedPlan && (
        <Text>Selected Plan: {selectedPlan.stripeProductName}</Text>
      )} */}
    </Flex>
  );
};

export default Subscribe;
